import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  AddonActionType,
  AddonContext,
  AddOnService,
  IAddonPayload,
} from 'src/app/core/services/add-on.service';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { opacityInOut } from '../../animations/opacity-in-out.animation';
import { scaleInOut } from '../../animations/scale-in-out.animation';
import { HelperService } from 'src/app/core/services/helper.service';

type AddonPurchaseModalOutput = boolean | { msg: AddonActionType } | null;

@Component({
  selector: 'addon-purchase-modal',
  templateUrl: './addon-purchase-modal.component.html',
  styleUrls: ['./addon-purchase-modal.component.scss'],
  animations: [opacityInOut, scaleInOut],
})
export class AddonPurchaseModalComponent implements OnInit, OnDestroy {
  @Input() featureId: string = '';
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() price: number | null = null;
  @Input() context: AddonContext = {};
  @Input() successMessage: string = '';
  @Input() quantity: number = 1;

  success: string | null = null;
  showResult = false;
  error: string | null = null;
  isLoading = false;
  isAccountAdmin = false;

  @Output() onChanged = new EventEmitter<AddonPurchaseModalOutput>();

  private destroy$ = new Subject<void>();

  constructor(
    private addOnService: AddOnService,
    private translate: TranslateService,
    private helperService: HelperService
  ) {}

  async ngOnInit(): Promise<void> {
    this.helperService.blockScroll$.next(true);
    try {
      this.isAccountAdmin = await this.addOnService.isAccountAdmin();
    } catch (error) {
      console.error('Error initializing addon modal:', error);
      this.handleError(error);
    }
  }

  close(): void {
    this.onChanged.emit(null);
  }

  async handleAction(): Promise<void> {
    if (!this.featureId || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.success = null;
    this.showResult = false;

    try {
      const result = await this.executeAddonAction();
      this.handleSuccess(result);
    } catch (error: unknown) {
      this.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async executeAddonAction(): Promise<AddonActionType> {
    try {
      const payload: IAddonPayload = {
        featureId: this.featureId,
        quantity: this.quantity,
        context: this.context,
      };
      const request$ = this.isAccountAdmin
        ? this.addOnService.purchaseAddon(payload)
        : this.addOnService.requestAddon(payload);

      await firstValueFrom(request$.pipe(takeUntil(this.destroy$)));
      return this.isAccountAdmin ? 'purchased' : 'requested';
    } catch (error) {
      console.error('Error executing addon action:', error);
      throw error;
    }
  }

  handleSuccess(actionType: AddonActionType): void {
    this.success = this.successMessage || this.getDefaultSuccessMessage();
    this.showResult = true;
    this.onChanged.emit({ msg: actionType });
  }

  getDefaultSuccessMessage(): string {
    const defaultKey = this.isAccountAdmin
      ? 'AddOns.Messages.PurchaseSuccess'
      : 'AddOns.Messages.RequestSuccess';

    const featureName = this.featureId
      ? this.translate.instant(`AddOns.Features.${this.featureId}`)
      : '';
      
    const params = {
      Feature: featureName,
    };

    return this.translate.instant(defaultKey, params);
  }

  handleError(error: any): void {
    console.error('Add-on operation failed');
    this.error =
      error?.error?.error ||
      this.translate.instant('AddOns.Errors.GenericError');
    this.success = null;
  }

  getActionButtonText(): string {
    if (this.isLoading) {
      return this.isAccountAdmin
        ? 'AddOns.Actions.Buying'
        : 'AddOns.Actions.Requesting';
    }
    return this.isAccountAdmin
      ? 'AddOns.Actions.Buy'
      : 'AddOns.Actions.Request';
  }

  ngOnDestroy(): void {
    this.helperService.blockScroll$.next(false);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
